import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList
} from 'recharts';
import { CalendarDays, RefreshCw, Bot, CheckCircle2, ArrowLeft, ClipboardCopy } from 'lucide-react';
import { getDashboardDates, getDashboardReport } from '../services/api';
import html2canvas from 'html2canvas';

const STATUS_COLORS = {
  Passed: '#2d8a4e', Failed: '#c0392b', Blocked: '#8b4513',
  Untested: '#c9c1b4', Retest: '#d4a017', Skipped: '#8a8a8a',
  'Not Applicable': '#6b6356', Clarification: '#8b6914', Deferred: '#1a1a1a',
  'In Progress': '#00674b', 'Dev Test Pending': '#4f7b8c', Descope: '#5d4037'
};
const PRIORITY_COLORS = ['#c0392b', '#d4a017', '#00674b', '#4f7b8c', '#8a8a8a'];
const TEAM_COLORS = ['#00674b', '#8b6914', '#4f7b8c', '#c0392b', '#2d8a4e', '#d4a017'];
const BUG_PRIORITY_COLORS = { 'Blocker': '#1a1a2e', 'High': '#c0392b', 'Medium': '#d4a017', 'Low': '#2d8a4e', 'Unset': '#8a8a8a' };

// Milestone tabs — all use the same TestRail run; M5 is greyed out.
// Run IDs are stable TestRail identifiers. Update runId and disabled flag
// when new milestone runs are created in TestRail.
const MILESTONES = [
  { key: 'M3', label: 'Milestone 3', runId: 72, disabled: false },
  { key: 'M4', label: 'Milestone 4', runId: 76, disabled: false },
  { key: 'M5', label: 'Milestone 5', runId: 72, disabled: true },
  { key: 'M6', label: 'Milestone 6', runId: 108, disabled: false },
  { key: 'FF', label: 'Full Fit', runId: 78, disabled: false, folder: 'FullFit' },
  // M7 is a TestRail-only milestone (no Taskei). Instead of one aggregated run
  // it shows individual TestRail runs grouped by touchpoint (POS / SCO) and
  // stage (Beta / Gamma). `layout: 'runGroups'` switches TestReport to the
  // grouped POS/SCO renderer defined by M7_RUN_GROUPS below.
  { key: 'M7', label: 'Milestone 7', disabled: false, layout: 'runGroups' }
];

// M7 grouped layout: two touchpoint columns (POS, SCO), each with Beta/Gamma
// stage groups. Every leaf is one TestRail run rendered as its own chart set.
// Update runId here when new M7 runs are created in TestRail. Any run id listed
// here must also be present in the CDK `TESTRAIL_RUN_IDS` env var so the nightly
// job generates its run_<id>_*.csv files.
const M7_RUN_GROUPS = [
  {
    touchpoint: 'POS',
    stages: [
      { stage: 'Beta', runs: [{ label: 'Smoke', runId: 131 }] },
      { stage: 'Gamma', runs: [{ label: 'Smoke', runId: 169 }, { label: 'Regression', runId: 135 }] }
    ]
  },
  {
    touchpoint: 'SCO',
    stages: [
      { stage: 'Beta', runs: [{ label: 'Smoke', runId: 132 }] },
      { stage: 'Gamma', runs: [{ label: 'Regression', runId: 136 }] }
    ]
  }
];

function colorFor(map, key, fallbackIndex, fallbackPalette) {
  return map[key] || fallbackPalette[fallbackIndex % fallbackPalette.length];
}
const CT_TZ = 'America/Chicago';
function today() { const parts = new Intl.DateTimeFormat('en-CA', { timeZone: CT_TZ }).format(new Date()).split('/'); return parts[0]; /* yyyy-mm-dd */ }
function formatCT(dateStr) { return new Date(dateStr).toLocaleString('en-US', { timeZone: CT_TZ }); }
function formatCTTime(dateStr) { return new Date(dateStr).toLocaleTimeString('en-US', { timeZone: CT_TZ, hour: '2-digit', minute: '2-digit' }); }
function EmptyState({ message }) { return <div className="tr-empty"><p>{message}</p></div>; }
function Panel({ title, subtitle, children }) {
  return (<div className="tr-panel"><div className="tr-panel-head"><h3>{title}</h3>{subtitle ? <span className="tr-panel-sub">{subtitle}</span> : null}</div><div className="tr-panel-body">{children}</div></div>);
}

function OverallStatusDonut({ data }) {
  if (!data || data.length === 0) return <EmptyState message="No status data." />;
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <ResponsiveContainer width="100%" height={320}>
      <PieChart>
        <Pie data={data} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={70} outerRadius={110}
          label={(e) => { const pct = (e.count/total)*100; return pct < 3 ? null : `${e.count} (${pct.toFixed(0)}%)`; }} labelLine={false}>
          {data.map((entry, i) => <Cell key={entry.status} fill={colorFor(STATUS_COLORS, entry.status, i, PRIORITY_COLORS)} />)}
        </Pie>
        <Tooltip formatter={(v, name) => [`${v} (${((v/total)*100).toFixed(1)}%)`, name]} />
        <Legend verticalAlign="bottom" height={36} />
        <text x="50%" y="44%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '1.4rem', fontWeight: 700, fill: '#2c2c2c' }}>{total}</text>
        <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '0.75rem', fill: '#6b6356' }}>Total</text>
      </PieChart>
    </ResponsiveContainer>
  );
}

function BugsByPriorityDonut({ data, resolvedCount }) {
  if (!data || data.length === 0) return <EmptyState message="No bug data." />;
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div>
      <ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie data={data} dataKey="count" nameKey="priority" cx="50%" cy="50%" innerRadius={70} outerRadius={110}
            label={(e) => `${e.priority}: ${e.count}`} labelLine={false}>
            {data.map((entry, i) => <Cell key={entry.priority} fill={PRIORITY_COLORS[i % PRIORITY_COLORS.length]} />)}
          </Pie>
          <Tooltip /><Legend verticalAlign="bottom" height={36} />
          <text x="50%" y="44%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '1.4rem', fontWeight: 700, fill: '#2c2c2c' }}>{total}</text>
          <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '0.75rem', fill: '#6b6356' }}>Open</text>
        </PieChart>
      </ResponsiveContainer>
      {resolvedCount !== undefined && (
        <div className="tr-resolved-badge"><CheckCircle2 size={18} /><span className="tr-resolved-count">{resolvedCount}</span><span className="tr-resolved-label">Resolved</span></div>
      )}
    </div>
  );
}

function BugsByTeamDonut({ data }) {
  if (!data || data.length === 0) return <EmptyState message="No outstanding bugs by team." />;
  const byTeam = {}; for (const r of data) byTeam[r.team] = (byTeam[r.team] || 0) + r.count;
  const rows = Object.entries(byTeam).map(([team, count]) => ({ team, count }));
  const total = rows.reduce((s, d) => s + d.count, 0);
  return (
    <ResponsiveContainer width="100%" height={320}>
      <PieChart>
        <Pie data={rows} dataKey="count" nameKey="team" cx="50%" cy="50%" innerRadius={70} outerRadius={110}
          label={(e) => `${e.team}: ${e.count}`} labelLine={false}>
          {rows.map((entry, i) => <Cell key={entry.team} fill={TEAM_COLORS[i % TEAM_COLORS.length]} />)}
        </Pie>
        <Tooltip /><Legend verticalAlign="bottom" height={36} />
        <text x="50%" y="44%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '1.4rem', fontWeight: 700, fill: '#2c2c2c' }}>{total}</text>
        <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '0.75rem', fill: '#6b6356' }}>Total</text>
      </PieChart>
    </ResponsiveContainer>
  );
}

function TestsByPriorityStackedBar({ data }) {
  if (!data || data.length === 0) return <EmptyState message="No test case data." />;
  const priorities = Array.from(new Set(data.map(d => d.priority)));
  const statuses = Array.from(new Set(data.map(d => d.status)));
  const rows = priorities.map(p => { const row = { priority: p }; statuses.forEach(s => { row[s] = 0; }); data.filter(d => d.priority === p).forEach(d => { row[d.status] = d.count; }); return row; });
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, left: 24, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis type="category" dataKey="priority" /><Tooltip /><Legend />
        {statuses.map((s, i) => <Bar key={s} dataKey={s} stackId="a" fill={colorFor(STATUS_COLORS, s, i, PRIORITY_COLORS)}><LabelList dataKey={s} position="center" formatter={(v) => (v > 0 ? v : '')} fill="#fff" /></Bar>)}
      </BarChart>
    </ResponsiveContainer>
  );
}

function TestsByModuleStackedBar({ data }) {
  if (!data || data.length === 0) return <EmptyState message="No module data." />;
  const modules = Array.from(new Set(data.map(d => d.module)));
  const statuses = Array.from(new Set(data.map(d => d.status)));
  const rows = modules.map(m => { const row = { module: m }; statuses.forEach(s => { row[s] = 0; }); data.filter(d => d.module === m).forEach(d => { row[d.status] = d.count; }); return row; });
  return (
    <ResponsiveContainer width="100%" height={Math.max(320, modules.length * 50)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, left: 80, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis type="category" dataKey="module" width={75} /><Tooltip /><Legend />
        {statuses.map((s, i) => <Bar key={s} dataKey={s} stackId="a" fill={colorFor(STATUS_COLORS, s, i, PRIORITY_COLORS)}><LabelList dataKey={s} position="center" formatter={(v) => (v > 0 ? v : '')} fill="#fff" /></Bar>)}
      </BarChart>
    </ResponsiveContainer>
  );
}

function BugsPriorityByTeamBar({ data }) {
  if (!data || data.length === 0) return <EmptyState message="No bug priority data by team." />;
  const teams = Array.from(new Set(data.map(d => d.team)));
  const priorities = Array.from(new Set(data.map(d => d.priority)));
  const rows = teams.map(t => { const row = { team: t }; priorities.forEach(p => { row[p] = 0; }); data.filter(d => d.team === t).forEach(d => { row[d.priority] = d.count; }); return row; });
  return (
    <ResponsiveContainer width="100%" height={Math.max(320, teams.length * 50)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, left: 100, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis type="category" dataKey="team" width={95} /><Tooltip /><Legend />
        {priorities.map((p, i) => <Bar key={p} dataKey={p} stackId="a" fill={BUG_PRIORITY_COLORS[p] || PRIORITY_COLORS[i % PRIORITY_COLORS.length]}><LabelList dataKey={p} position="center" formatter={(v) => (v > 0 ? `${p}-${v}` : '')} fill="#fff" /></Bar>)}
      </BarChart>
    </ResponsiveContainer>
  );
}

function AutomationBugsPanel({ count, bugs }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="tr-panel tr-automation-panel">
      <div className="tr-panel-head"><h3><Bot size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />Bugs Found by Automation</h3><span className="tr-panel-sub">{count} bug{count !== 1 ? 's' : ''}</span></div>
      <div className="tr-panel-body">
        <div className="tr-automation-stat"><span className="tr-automation-count">{count}</span><span className="tr-automation-label">Defects detected by automated testing</span></div>
        {bugs && bugs.length > 0 && (<>
          <button className="btn btn-secondary btn-sm tr-automation-toggle" onClick={() => setExpanded(!expanded)}>{expanded ? 'Hide Details' : 'Show Details'}</button>
          {expanded && (<div className="tr-bug-table-scroll" style={{ marginTop: 12 }}><table className="tr-bug-table"><thead><tr><th>Bug ID</th><th>Title</th><th>Priority</th><th>Status</th></tr></thead><tbody>
            {bugs.map((bug, i) => (<tr key={bug.shortId || i}><td><a href={`https://taskei.amazon.dev/tasks/${bug.shortId}`} target="_blank" rel="noopener noreferrer" className="tr-bug-link">{bug.shortId}</a></td><td>{bug.title}</td><td><span className={`badge badge-prio-${(bug.priority||'').toLowerCase()}`}>{bug.priority}</span></td><td>{bug.workflowStatus}</td></tr>))}
          </tbody></table></div>)}
        </>)}
      </div>
    </div>
  );
}

function BugDetailsTable({ data, targetedReleases }) {
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [targetedReleaseFilter, setTargetedReleaseFilter] = useState('');
  if (!data || data.length === 0) return <EmptyState message="No bug details available." />;
  const priorities = Array.from(new Set(data.map(d => d.priority))).sort();
  const statuses = Array.from(new Set(data.map(d => d.workflowStatus))).sort();
  const releases = targetedReleases && targetedReleases.length > 0 ? targetedReleases : Array.from(new Set(data.map(d => d.targetedRelease).filter(Boolean))).sort();
  const hasTargetedRelease = releases.length > 0;
  const filtered = data.filter(bug => (!priorityFilter || bug.priority === priorityFilter) && (!statusFilter || bug.workflowStatus === statusFilter) && (!targetedReleaseFilter || bug.targetedRelease === targetedReleaseFilter));
  const taskeiBaseUrl = 'https://taskei.amazon.dev/tasks';
  return (
    <div className="tr-bug-table-wrapper">
      <div className="tr-bug-filters">
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="input tr-filter-select"><option value="">All Priorities</option>{priorities.map(p => <option key={p} value={p}>{p}</option>)}</select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input tr-filter-select"><option value="">All Statuses</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}</select>
        {hasTargetedRelease && (<select value={targetedReleaseFilter} onChange={(e) => setTargetedReleaseFilter(e.target.value)} className="input tr-filter-select"><option value="">All Targeted Milestones</option>{releases.map(r => <option key={r} value={r}>{r}</option>)}</select>)}
        <span className="tr-filter-count">{filtered.length} of {data.length} bugs</span>
      </div>
      <div className="tr-bug-table-scroll"><table className="tr-bug-table"><thead><tr><th>Created</th><th>Bug Title</th><th>Priority</th><th>Status</th></tr></thead><tbody>
        {filtered.map((bug, i) => (<tr key={bug.shortId || i}><td className="tr-bug-date">{bug.createdAt ? formatCT(bug.createdAt) : ''}</td><td><a href={`${taskeiBaseUrl}/${bug.shortId}`} target="_blank" rel="noopener noreferrer" className="tr-bug-link">{bug.title || bug.shortId}</a></td><td><span className={`badge badge-prio-${(bug.priority||'').toLowerCase()}`}>{bug.priority}</span></td><td>{bug.workflowStatus}</td></tr>))}
        {filtered.length === 0 && (<tr><td colSpan={4} className="tr-bug-empty">No bugs match the selected filters.</td></tr>)}
      </tbody></table></div>
    </div>
  );
}

// ── M7 grouped (POS/SCO) layout ────────────────────────────────────────────
// Renders one TestRail run as a compact chart set (donut + priority + module),
// laid out so multiple runs sit side by side within a stage.
function RunChartSet({ run }) {
  const rep = run.report;
  // Full path label, e.g. "POS-Beta-Smoke" / "SCO-Gamma-Regression".
  const fullLabel = [run.touchpoint, run.stage, run.label].filter(Boolean).join('-');
  if (!rep) return <div className="tr-run-card tr-run-loading">Loading {fullLabel} (run {run.runId})…</div>;
  if (!rep.available) {
    return (
      <div className="tr-run-card tr-run-empty">
        <div className="tr-run-card-head"><span className="tr-run-label">{fullLabel}</span><span className="tr-run-id">Run {run.runId}</span></div>
        <EmptyState message={`No data for run ${run.runId} on this date.`} />
      </div>
    );
  }
  return (
    <div className="tr-run-card">
      <div className="tr-run-card-head">
        <span className="tr-run-label">{fullLabel}</span>
        <span className="tr-run-id" title={rep.runName || ''}>Run {rep.runId}{rep.total ? ` · ${rep.total} total` : ''}</span>
      </div>
      <Panel title="Overall Test Case Status" subtitle={`${rep.total} total`}><OverallStatusDonut data={rep.overall} /></Panel>
      <Panel title="Test Cases by Priority"><TestsByPriorityStackedBar data={rep.testsByPriority} /></Panel>
    </div>
  );
}

// A stage group (e.g. "Beta" or "Gamma") holding one or more runs side by side.
function StageGroup({ touchpoint, stage, runs }) {
  return (
    <div className="tr-stage-group">
      <h4 className="tr-stage-title">{stage}</h4>
      <div className="tr-stage-runs">
        {runs.map((run) => <RunChartSet key={`${run.label}-${run.runId}`} run={{ ...run, touchpoint, stage }} />)}
      </div>
    </div>
  );
}

// The full M7 body: POS and SCO touchpoint columns, each with stage groups.
// Each leaf card's report is looked up from `runReports` by its runId.
function RunGroupsLayout({ groups, runReports }) {
  return (
    <div className="tr-grid tr-grid-groups">
      {groups.map((group) => (
        <div key={group.touchpoint} className="tr-col tr-col-touchpoint">
          <h3 className="tr-col-title">{group.touchpoint}</h3>
          {group.stages.map((stg) => (
            <StageGroup
              key={stg.stage}
              touchpoint={group.touchpoint}
              stage={stg.stage}
              runs={stg.runs.map((r) => ({ ...r, report: runReports[r.runId] }))}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function TestReport() {
  const navigate = useNavigate();
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today());
  const [selectedMilestone, setSelectedMilestone] = useState('M4');
  const [report, setReport] = useState(null);
  // For grouped (M7) layout: real reports keyed by { [runId]: reportObject }
  const [runReports, setRunReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const reportRef = useRef(null);
  const chartsRef = useRef(null);
  const currentMilestoneConfig = MILESTONES.find(m => m.key === selectedMilestone) || MILESTONES[0];
  const isRunGroups = currentMilestoneConfig.layout === 'runGroups';
  // Unique run IDs needed for the grouped (M7) layout — de-duplicated because a
  // run (e.g. 136) can appear in more than one stage/column.
  const groupRunIds = useMemo(() => {
    if (!isRunGroups) return [];
    const ids = new Set();
    M7_RUN_GROUPS.forEach(g => g.stages.forEach(s => s.runs.forEach(r => ids.add(r.runId))));
    return Array.from(ids);
  }, [isRunGroups]);
  const minDate = useMemo(() => (availableDates.length ? availableDates[availableDates.length - 1] : undefined), [availableDates]);
  const maxDate = today();


  const handleCopyToClipboard = async () => {
    if (!chartsRef.current || !report?.available) return;
    setCopied(false);

    const escapeHtml = (str) => {
      if (!str) return '';
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    };

    try {
      // Capture only the charts section as an image (excludes bug details table)
      const canvas = await html2canvas(chartsRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgDataUrl = canvas.toDataURL('image/png');

      // Build HTML with chart image + clickable links table
      const bugRows = (report.bugDetails || []).map(bug => {
        const link = `https://taskei.amazon.dev/tasks/${bug.shortId}`;
        const priorityColor = bug.priority === 'High' ? '#c62828' : bug.priority === 'Medium' ? '#e65100' : '#2e7d32';
        return `<tr>
          <td style="padding:6px 10px;border-bottom:1px solid #eee"><a href="${link}" style="color:#1565c0;text-decoration:none;font-weight:500">${escapeHtml(bug.title || bug.shortId)}</a></td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee;color:${priorityColor};font-weight:600">${escapeHtml(bug.priority || 'Unset')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(bug.module || '—')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(bug.assigned || '—')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(bug.teams || '—')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${bug.leadDays ? bug.leadDays + 'd' : '—'}</td>
        </tr>`;
      }).join('');

      const html = `
        <div style="font-family:Arial,sans-serif;max-width:1200px">
          <h2 style="margin:0 0 8px">Apex ${currentMilestoneConfig.label} - Test Report</h2>
          <p style="color:#666;margin:0 0 16px">Date: ${selectedDate} | Total Tests: ${report.total || 0} | Open Bugs: ${report.bugDetails?.length || 0} | Resolved: ${report.resolvedCount || 0}</p>
          <img src="${imgDataUrl}" style="width:100%;max-width:1200px;border:1px solid #eee;border-radius:8px" />
          <h3 style="margin:24px 0 12px">Open Bug Details (clickable links)</h3>
          <table style="width:100%;border-collapse:collapse;font-size:13px">
            <thead>
              <tr style="background:#f5f5f5">
                <th style="padding:8px 10px;text-align:left;border-bottom:2px solid #ddd">Title</th>
                <th style="padding:8px 10px;text-align:left;border-bottom:2px solid #ddd">Priority</th>
                <th style="padding:8px 10px;text-align:left;border-bottom:2px solid #ddd">Module</th>
                <th style="padding:8px 10px;text-align:left;border-bottom:2px solid #ddd">Assigned</th>
                <th style="padding:8px 10px;text-align:left;border-bottom:2px solid #ddd">Teams</th>
                <th style="padding:8px 10px;text-align:left;border-bottom:2px solid #ddd">Lead</th>
              </tr>
            </thead>
            <tbody>${bugRows}</tbody>
          </table>
        </div>
      `;

      // Copy as HTML to clipboard (Outlook supports rich HTML paste)
      const blob = new Blob([html], { type: 'text/html' });
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': blob })
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Copy to clipboard failed:', err);
      alert('Failed to copy to clipboard. Your browser may not support this feature.');
    }
  };

  useEffect(() => { getDashboardDates().then((res) => { const dates = res?.dates ?? []; setAvailableDates(dates); if (dates.length > 0) setSelectedDate(dates[0]); }).catch(() => setAvailableDates([])); }, []);

  useEffect(() => {
    let active = true; setLoading(true); setError(null);

    if (isRunGroups) {
      // Grouped (M7) layout: fetch every unique run for the date in parallel.
      // No Taskei/milestone folder — these are pure per-run TestRail reports.
      setReport(null);
      Promise.all(
        groupRunIds.map((runId) =>
          getDashboardReport(selectedDate, runId)
            .then((res) => [runId, res])
            .catch(() => [runId, { available: false, runId }])
        )
      )
        .then((entries) => {
          if (!active) return;
          const map = {};
          entries.forEach(([runId, res]) => { map[runId] = res; });
          setRunReports(map);
        })
        .catch((err) => { if (active) setError(err.message || 'Failed to load report'); })
        .finally(() => { if (active) setLoading(false); });
      return () => { active = false; };
    }

    // Standard single-run layout (M3/M4/M6/FF) with Taskei column.
    setRunReports({});
    getDashboardReport(selectedDate, currentMilestoneConfig.runId, currentMilestoneConfig.folder || selectedMilestone)
      .then((res) => { if (active) setReport(res); })
      .catch((err) => { if (active) setError(err.message || 'Failed to load report'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedDate, selectedMilestone, currentMilestoneConfig.runId, isRunGroups, groupRunIds, refreshKey]);

  // Grouped layout has data if any real run returned a report.
  const groupHasData = isRunGroups && groupRunIds.some((id) => runReports[id]?.available);
  const hasData = isRunGroups ? groupHasData : report?.available;
  return (
    <div className="test-report">
      <button className="btn btn-secondary btn-sm back-to-dashboards" onClick={() => navigate('/dashboards')}><ArrowLeft size={14} /> Back to Dashboards</button>
      <div className="tr-header">
        <div><h2>Apex {currentMilestoneConfig.label} - Test Report</h2></div>
        <div className="tr-controls">
          <div className="tr-milestone-tabs">
            {MILESTONES.map(m => (<button key={m.key} className={`tr-milestone-tab ${selectedMilestone === m.key ? 'tr-milestone-tab-active' : ''} ${m.disabled ? 'tr-milestone-tab-disabled' : ''}`} onClick={() => !m.disabled && setSelectedMilestone(m.key)} disabled={m.disabled} title={m.disabled ? 'Coming soon' : m.label}>{m.key}</button>))}
          </div>
          <label className="tr-date"><CalendarDays size={16} /><input type="date" value={selectedDate} min={minDate} max={maxDate} onChange={(e) => setSelectedDate(e.target.value)} className="input" /></label>
          <button className="btn btn-secondary btn-sm" onClick={() => setRefreshKey(k => k + 1)} disabled={loading} title="Refresh"><RefreshCw size={14} /> Refresh</button>
          {!isRunGroups && (
            <button className="btn btn-secondary btn-sm" onClick={handleCopyToClipboard} disabled={loading || !report?.available} title="Copy dashboard as image to clipboard"><ClipboardCopy size={14} /> {copied ? '✓ Copied!' : 'Copy to Clipboard'}</button>
          )}
        </div>
      </div>
      {!loading && hasData && !isRunGroups && report?.lastRefreshed && (
        <div className="tr-refresh-info"><span>Last data refreshed: {formatCT(report.lastRefreshed)}</span><span>Next data refresh: {(() => { const next = new Date(report.lastRefreshed); next.setMinutes(0,0,0); next.setHours(next.getHours()+1); return formatCT(next); })()}</span></div>
      )}
      {loading ? (<div className="tr-loading">Loading report for {selectedDate}…</div>
      ) : error ? (<div className="error-message">{error}</div>
      ) : !hasData ? (<div className="tr-nodata"><h3>No data yet</h3><p>No report has been generated for <strong>{selectedDate}</strong> ({selectedMilestone}). The nightly job runs at midnight Central Time.</p></div>
      ) : isRunGroups ? (<div ref={reportRef}>
        <RunGroupsLayout groups={M7_RUN_GROUPS} runReports={runReports} />
      </div>
      ) : (<div ref={reportRef}>
        <div className="tr-grid" ref={chartsRef}>
          <div className="tr-col tr-col-testrail">
            <h3 className="tr-col-title">TestRail</h3>
            <Panel title="Overall Test Case Status" subtitle={`${report.total} total`}><OverallStatusDonut data={report.overall} /></Panel>
            <Panel title="Test Cases by Priority"><TestsByPriorityStackedBar data={report.testsByPriority} /></Panel>
            <Panel title="Test Cases by Module"><TestsByModuleStackedBar data={report.testsByModule} /></Panel>
          </div>
          <div className="tr-col tr-col-taskei">
            <h3 className="tr-col-title">Taskei</h3>
            <Panel title="Bug Count by Priority" subtitle={`Filtered: ${selectedMilestone}`}><BugsByPriorityDonut data={report.bugsByPriority} resolvedCount={report.resolvedCount} /></Panel>
            <Panel title="Outstanding Bugs by Team"><BugsByTeamDonut data={report.bugsByTeam} /></Panel>
            <Panel title="Bug Priority by Team"><BugsPriorityByTeamBar data={report.bugsByTeamPriority} /></Panel>
            <AutomationBugsPanel count={report.automationBugsCount || 0} bugs={report.automationBugs || []} />
          </div>
        </div>
        <div className="tr-bug-section">
          <Panel title="Bug Details" subtitle={`${(report.bugDetails || []).length} bugs`}><BugDetailsTable data={report.bugDetails} targetedReleases={report.targetedReleases} /></Panel>
        </div>
      </div>)}
    </div>
  );
}

export default TestReport;
